import {FunctionComponent, useCallback, useMemo, useState} from 'react';
import {faker} from '@faker-js/faker';
import {useMutation} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';
import {DocumentContext, DocumentContextType} from '@/components/document/context/document-context';
import {createDocumentStore} from '@/components/document/store/document-store';
import Checkbox from '@/components/form/checkbox';
import Combobox from '@/components/form/combobox/combobox';
import ComboboxAsync from '@/components/form/combobox/combobox-async';
import {ComboboxValue} from '@/components/form/combobox/combobox-content';
import CurrencyInput from '@/components/form/currency-input';
import FieldWrapper from '@/components/form/field-wrapper';
import FormControl from '@/components/form/form-control';
import Input from '@/components/form/input';
import MaskedInput from '@/components/form/masked-input';
import PercentageInput from '@/components/form/percentage-input';
import Radio from '@/components/form/radio';
import Select from '@/components/form/select';
import {logger} from '@/initializers/logger';

const mockComboboxValues = Array.from({length: 50}).map((_, id) => ({
  id: `combobox-${id}`,
  label: faker.person.fullName(),
  value: `${id}`,
}));

const comboboxVariantsProps = [
  {name: 'Default'},
  {name: 'Error', isInError: true},
  {name: 'Disabled', isDisabled: true},
  {name: 'Warning', warningMessage: 'Be warned!'},
] as const;

const Forms: FunctionComponent = () => {
  const store = useMemo(() => createDocumentStore(), []);
  const documentContext = useMemo(() => ({store}) as unknown as DocumentContextType, [store]);

  const handlePercentageChange = useCallback((value: string) => {
    logger.log('Percentage value', value);
  }, []);

  const [showComboboxVariants, setShowComboboxVariants] = useState(false);
  const [showComboboxAsyncVariants, setShowComboboxAsyncVariants] = useState(false);

  const [selectedComboboxValue, setSelectedComboboxValue] = useState<ComboboxValue | null>(null);

  const [searchResults, setSearchResults] = useState<ComboboxValue[]>([]);
  const searchComboboxAsyncMutation = useSearchComboboxAsyncMutation(mockComboboxValues);
  const [selectedComboboxAsyncValue, setSelectedComboboxAsyncValue] = useState<ComboboxValue | null>(null);

  const {t} = useTranslation();

  return (
    <DocumentContext.Provider value={documentContext}>
      <ul className="flex flex-col gap-8">
        <li>
          <h3 className="mb-4 font-semibold text-charcoal">Input obligatoire</h3>

          <FormControl isRequired>
            <FieldWrapper identifier="test4321" label="Téléphone">
              <MaskedInput type="tel" mask="### ###-####" placeholder="999 929-9999" />
            </FieldWrapper>
          </FormControl>
        </li>

        <li>
          <h3 className="mb-4 font-semibold text-charcoal">Input en erreur</h3>

          <FormControl isInError isRequired>
            <FieldWrapper
              identifier="test1234"
              label="Nom complet"
              helpDialogContent={t('routes.debug.design-system.forms.fake-help-dialog')}
            >
              <Input placeholder="Entrez votre nom complet" id="test1234" />
            </FieldWrapper>
          </FormControl>
        </li>

        <li>
          <h3 className="mb-4 font-semibold text-charcoal">
            Input obligatoire avec un masque de type <code>date</code>
          </h3>

          <FormControl isRequired>
            <FieldWrapper identifier="Date" label="Date">
              <MaskedInput type="date" />
            </FieldWrapper>
          </FormControl>
        </li>

        <li>
          <h3 className="mb-4 font-semibold text-charcoal">Input disabled</h3>

          <FormControl isRequired isDisabled>
            <FieldWrapper identifier="test1234" label="Nom complet">
              <Input placeholder="YYYY-MM-DD" />
            </FieldWrapper>
          </FormControl>
        </li>

        <li>
          <h3 className="mb-4 font-semibold text-charcoal">Input readonly</h3>

          <FormControl isRequired>
            <FieldWrapper identifier="date" label="Date">
              <Input readOnly value="2024-08-09" placeholder="YYYY-MM-DD" />
            </FieldWrapper>
          </FormControl>
        </li>

        <li>
          <h3 className="mb-4 font-semibold text-charcoal">Input – Currency</h3>

          <FormControl isRequired>
            <FieldWrapper identifier="currency-input" label="Montant du prêt">
              <CurrencyInput id="currency-input" placeholder="$0.00" />
            </FieldWrapper>
          </FormControl>
        </li>

        <li>
          <h3 className="mb-4 font-semibold text-charcoal">Input – Percentage</h3>

          <FormControl isRequired>
            <FieldWrapper identifier="percentage-input" label="Pourcentage">
              <PercentageInput onChange={event => handlePercentageChange(event.target?.value)} placeholder="0" />
            </FieldWrapper>
          </FormControl>
        </li>

        <li>
          <hr className="bg-charcoal" />
        </li>

        <li>
          <h3 className="mb-4 font-semibold text-charcoal">Checkbox</h3>

          <FormControl isDisabled>
            <Checkbox label="Je suis d'accord avec les termes et conditions" />
          </FormControl>

          <FormControl>
            <Checkbox defaultChecked label="Je suis d'accord avec les termes et conditions" />
          </FormControl>

          <FormControl>
            <Checkbox label="Je suis d'accord avec les termes et conditions" />
          </FormControl>
        </li>

        <li>
          <hr className="bg-charcoal" />
        </li>

        <li>
          <h3 className="mb-4 font-semibold text-charcoal">Radio</h3>

          <FormControl isDisabled>
            <Radio label="Je suis d'accord avec les termes et conditions" />
          </FormControl>

          <FormControl>
            <Radio defaultChecked label="Je suis d'accord avec les termes et conditions" />
          </FormControl>

          <FormControl>
            <Radio label="Je suis d'accord avec les termes et conditions" />
          </FormControl>
        </li>

        <li>
          <hr className="bg-charcoal" />
        </li>

        <li>
          <h3 className="mb-4 font-semibold text-charcoal">Select</h3>

          <FormControl>
            <FieldWrapper
              identifier="FieldWrapperSelect"
              label="Nom complet"
              helpDialogContent={t('routes.debug.design-system.forms.fake-help-dialog')}
            >
              <Select id="FieldWrapperSelect">
                <option value="1">Option 1</option>
                <option value="2">Option 2</option>
                <option value="3">Option 3</option>
              </Select>
            </FieldWrapper>
          </FormControl>

          <FormControl isDisabled>
            <Select>
              <option value="1">Option 1</option>
              <option value="2">Option 2</option>
              <option value="3">Option 3</option>
            </Select>
          </FormControl>

          <FormControl isInError>
            <Select>
              <option value="1">Option 1</option>
              <option value="2">Option 2</option>
              <option value="3">Option 3</option>
            </Select>
          </FormControl>
        </li>

        <li>
          <div className="flex w-full justify-between">
            <h3 className="mb-4 font-semibold text-charcoal">Combobox</h3>
            <button
              className="mb-4 rounded bg-violet px-4 py-2 text-xs text-white"
              onClick={() => setShowComboboxVariants(!showComboboxVariants)}
            >
              {showComboboxVariants ? 'Hide' : 'Show'} variants
            </button>
          </div>

          {!showComboboxVariants ? (
            <FormControl>
              <FieldWrapper identifier="test4321" label="Default">
                <Combobox
                  values={mockComboboxValues}
                  selectedValue={selectedComboboxValue}
                  onChange={value => setSelectedComboboxValue(value ?? null)}
                />
              </FieldWrapper>
            </FormControl>
          ) : (
            <>
              {comboboxVariantsProps.map(({name, ...props}, index) => (
                <FormControl key={index} {...props}>
                  <FieldWrapper identifier="test4321" label={name}>
                    <Combobox
                      values={mockComboboxValues}
                      selectedValue={selectedComboboxValue}
                      onChange={value => setSelectedComboboxValue(value ?? null)}
                    />
                  </FieldWrapper>
                </FormControl>
              ))}
            </>
          )}
        </li>

        <li>
          <div className="flex w-full justify-between">
            <h3 className="mb-4 font-semibold text-charcoal">Combobox async</h3>
            <button
              className="mb-4 rounded bg-violet px-4 py-2 text-xs text-white"
              onClick={() => setShowComboboxAsyncVariants(!showComboboxAsyncVariants)}
            >
              {showComboboxVariants ? 'Hide' : 'Show'} variants
            </button>
          </div>
          {!showComboboxAsyncVariants ? (
            <>
              <FormControl>
                <FieldWrapper identifier="test4322" label="Default">
                  <ComboboxAsync
                    data-testid="new-document-investor-select"
                    placeholder={'Write to search, write "empty" to show empty results'}
                    searchResults={searchResults}
                    setSearchResults={setSearchResults}
                    fetchSearchResultsMutation={searchComboboxAsyncMutation}
                    selectedValue={selectedComboboxAsyncValue}
                    onChange={value => setSelectedComboboxAsyncValue(value ?? null)}
                  />
                </FieldWrapper>
              </FormControl>
            </>
          ) : (
            <>
              {comboboxVariantsProps.map(({name, ...props}, index) => (
                <div key={index}>
                  <FormControl key={index} {...props}>
                    <FieldWrapper identifier="test4322" label={name}>
                      <ComboboxAsync
                        data-testid="new-document-investor-select"
                        placeholder={'Write to search, write "empty" to show empty results'}
                        searchResults={searchResults}
                        setSearchResults={setSearchResults}
                        fetchSearchResultsMutation={searchComboboxAsyncMutation}
                        selectedValue={selectedComboboxAsyncValue}
                        onChange={value => setSelectedComboboxAsyncValue(value ?? null)}
                      />
                    </FieldWrapper>
                  </FormControl>
                  {name !== 'Disabled' && (
                    <FormControl key={index} {...props}>
                      <FieldWrapper identifier="test4322" label={`${name} (Loading)`}>
                        <ComboboxAsync
                          data-testid="new-document-investor-select"
                          placeholder={'Write to search, write "empty" to show empty results'}
                          searchResults={searchResults}
                          setSearchResults={setSearchResults}
                          fetchSearchResultsMutation={searchComboboxAsyncMutation}
                          selectedValue={selectedComboboxAsyncValue}
                          onChange={value => setSelectedComboboxAsyncValue(value ?? null)}
                          isLoading={true}
                        />
                      </FieldWrapper>
                    </FormControl>
                  )}
                  <FormControl key={index} {...props}>
                    <FieldWrapper identifier="test4322" label={`${name} (Selected)`}>
                      <ComboboxAsync
                        data-testid="new-document-investor-select"
                        placeholder={'Write to search, write "empty" to show empty results'}
                        searchResults={searchResults}
                        setSearchResults={setSearchResults}
                        fetchSearchResultsMutation={searchComboboxAsyncMutation}
                        selectedValue={mockComboboxValues[0]}
                        onChange={value => setSelectedComboboxAsyncValue(value ?? null)}
                      />
                    </FieldWrapper>
                  </FormControl>
                </div>
              ))}
            </>
          )}
        </li>
      </ul>
    </DocumentContext.Provider>
  );
};

const getSearchInvestorsComboboxValues = async (values: ComboboxValue[], query: string): Promise<ComboboxValue[]> => {
  return new Promise(resolve => {
    setTimeout(() => {
      resolve(query === 'empty' ? [] : values);
    }, 1000);
  });
};

const useSearchComboboxAsyncMutation = (mockComboboxValues: ComboboxValue[]) =>
  useMutation({
    mutationKey: ['search-investors-as-combobox-values'],
    mutationFn: (query: string) => getSearchInvestorsComboboxValues(mockComboboxValues, query),
    retry: false,
  });

export default Forms;
